import { Component, Injector, OnInit, Output, EventEmitter, OnDestroy, ViewChild, Input, ViewChildren, QueryList } from '@angular/core';
import { Subscription } from 'rxjs';

// models
import { Catalog, MoveLot, MoveLotProperty } from '../../models/catalog';
import { Language } from '../../models/language';
import { Product, GridProperty, SystemProductPropertyTypesEnum } from '../../models/product';

// services
import { TranslateService } from '@ngx-translate/core';
import { ErrorService } from '../../services/error.service';
import { LanguageService } from '../../services/language.service';
import { SpinnerService } from '../../services/spinner.service';

import { MoveLotsConfirmationComponent } from './move-lots-confirmation.component';
import { ICatalogService } from '../../interfaces/catalog';
import { ActivatedRoute } from '@angular/router';
import { ApplicationSettings } from '../../models/application-settings';
import { WebApiService } from '../../services/web-api.service';
import { ServiceFactory } from '../../factory/service-factory';

@Component({
    selector: 'move-lots-component',
    templateUrl: 'move-lots.component.html',
    styleUrls: ['./move-lots.component.scss'],
})
export class MoveLotsComponent implements OnInit, OnDestroy {
  @Output() close = new EventEmitter<boolean>();
  @Input() catalogName: string;
  
  @ViewChild('moveLotsConfirmation') moveLotsConfirmation: MoveLotsConfirmationComponent;

  catalogId: number;
  auctionId: number;
  languages: Array<Language> = [];
  catalogs: Array<Catalog> = [];
  model: MoveLot;
  product: Product;
  JSON = JSON;
  translations: any;
  destinationCatalog: number;
  isSupplierView = false;
  supplierId: number;
  moveLotsConfirmationButtonDisabled: boolean;

  rtlEnabled = localStorage.getItem('last-selected-language-direction') ? JSON.parse(localStorage.getItem('last-selected-language-direction')) : false;
  private _subscription: Subscription;

  protected catalogService: ICatalogService;
  protected translate: TranslateService;
  protected errorService: ErrorService;
  protected spinner: SpinnerService;

  constructor(
    protected injector: Injector,
    protected route: ActivatedRoute,
    protected appSettings: ApplicationSettings,
    protected webApiService: WebApiService,
    private languageService: LanguageService
  ) {    
    this.catalogService = ServiceFactory.getCatalogService(route, appSettings, webApiService);
    this.translate = injector.get(TranslateService);
    this.errorService = injector.get(ErrorService);
    this.spinner = injector.get(SpinnerService);

    this._subscription = this.languageService.direction.subscribe(dir => {
      this.rtlEnabled = dir;
    });

    if (route.snapshot.parent.parent.url.toString() === 'supplier') {
      this.isSupplierView = true;       
      this.supplierId = +this.route.snapshot.paramMap.get('id');
    }    
  }

  ngOnInit() {
    this.translate.get('MOVE_LOTS').subscribe((res: string) => {
      this.translations = res;
    });

    this.translate.onLangChange.subscribe(() => {
      this.translate.get('MOVE_LOTS').subscribe((res: string) => {
        this.translations = res;
      });
    });
  }

  ngOnDestroy() {
    this._subscription.unsubscribe();
  }

  getLabel(prop: GridProperty) {
    const pp = this.product.productProperties.find(p => p.productPropertyId === prop.productPropertyId);

    if (pp) {
      return pp.name;
    }

    return '';
  }

  getType(prop: GridProperty) {
    const pp = this.product.productProperties.find(p => p.productPropertyId === prop.productPropertyId);

    if (pp) {
      return pp.propertyTypeId;
    }

    return 0;
  }

  parseLabelAndOptions() {
    this.product.moveLotsToCatalog.forEach(s => {
      var property = this.product.productProperties.find(pp => pp.productPropertyId == s.productPropertyId);
      s.label = this.getLabel(s);
      s.propertyTypeId = this.getType(s);
      s.masterDataListId = property.masterDataListId;
      s.hidden = this.isSupplierView && property.systemProductPropertyType === SystemProductPropertyTypesEnum.Supplier;
    });
  }

  clearSelections() {
    this.destinationCatalog = null;
    this.product.moveLotsToCatalog.forEach(s => {
      s.masterDataRowIdSelection = null;
      s.intValueSelection = null;
      s.intValueToSelection = null;
    });
  }

  open(auctionId: number,
      catalogId: number,
      languages: Array<Language>,
      product: Product,
      catalogs: Array<Catalog>) {
    this.auctionId = auctionId;
    this.catalogId = catalogId;
    this.languages = languages;
    this.product = product;
    this.catalogs = catalogs.filter(c => c.catalogId !== catalogId);

    this.model = new MoveLot();

    this.clearSelections();

    this.parseLabelAndOptions();
  }

  parseSelections() {
    this.model.catalogGroupFilterProperty = [];
    this.product.moveLotsToCatalog.forEach(s => {
      if (s.masterDataRowIdSelection || s.intValueSelection || s.intValueToSelection) {
        const catalogGroupFilterProperty = new MoveLotProperty();
        catalogGroupFilterProperty.productPropertyId = s.productPropertyId;
        catalogGroupFilterProperty.masterDataListRowIds =  s.masterDataRowIdSelection;
        catalogGroupFilterProperty.intValue = s.intValueSelection;
        catalogGroupFilterProperty.toIntValue = s.intValueToSelection;

        this.model.catalogGroupFilterProperty.push(catalogGroupFilterProperty);
      }
    });
  }

  save() {
    this.parseSelections();
    this.catalogService.getNumberOfLotsToMove(this.auctionId, this.catalogId, this.model)
    .subscribe(result => {
      this.openMoveLotsConfirmation(result);
    });
  }

  openMoveLotsConfirmation = (numberOfLots: number) => {
    this.moveLotsConfirmation.message = this.translate.instant('MOVE_LOTS.CONFIRMATION_MESSAGE',
      { amount: numberOfLots, sourceCatalog: this.catalogName, destinationCatalog: this.catalogs.find(_ => _.catalogId === this.destinationCatalog).name });
    
    this.moveLotsConfirmationButtonDisabled = numberOfLots === 0 ? true: false;
    this.moveLotsConfirmation.opened = true;    
  }

  moveLots() {
    this.spinner.show();
    this.catalogService.moveLots(this.auctionId, this.catalogId, this.destinationCatalog, this.model)
      .subscribe(s => {
        this.spinner.hide();
        this.cancel(true);
      }, e => {
        this.errorService.show(e);
        this.spinner.hide();
      });
  }

  cancel(isMoved: boolean) {
    this.clearSelections();
    this.close.emit(isMoved);
  }

  disabledButton() {
    if (this.destinationCatalog === null) {
      return true;
    } else {
      return false;
    }
  }
}
